import { useScrollAnimation } from '../../../hooks/useScrollAnimation';

const steps = [
  {
    number: '01',
    icon: 'ri-mic-line',
    title: 'Instrutores Falam no Microfone',
    description: 'Cada instrutor usa um microfone sem fio. Pode estar a metros de distância dos alunos — a voz é captada com clareza profissional. Fala em tom de voz natural, sem esforço.',
  },
  {
    number: '02',
    icon: 'ri-signal-tower-line',
    title: 'Áudio Transmitido em RF',
    description: 'Os transmissores enviam o áudio em radiofrequência para todos os fones simultaneamente. Latência abaixo de 5ms — a voz do instrutor chega em tempo real, sem delay perceptível.',
  },
  {
    number: '03',
    icon: 'ri-headphone-line',
    title: 'Alunos Selecionam Seu Canal',
    description: 'Cada aluno recebe um fone sem fio e seleciona o canal do seu instrutor. É só apertar um botão. O LED indica visualmente qual canal está ativo — simples e intuitivo.',
  },
  {
    number: '04',
    icon: 'ri-lightbulb-line',
    title: 'Aprendizado Imersivo e Focado',
    description: 'Com isolamento acústico passivo, o aluno mergulha no conteúdo. Sem ruído externo, sem conversas paralelas, sem dispersão. A retenção de conhecimento aumenta drasticamente.',
  },
];

const equipment = [
  {
    icon: 'ri-headphone-line',
    name: 'Fones para Aula Silenciosa',
    description: 'Headphones profissionais com receptor multi-canal, almofadas confortáveis para uso prolongado (até 10h de bateria), controle individual de volume e LED indicador de canal. Higienizados entre cada evento.',
    specs: ['Alcance até 200m indoor', 'Bateria 10h+', 'LED indicador de canal', 'Volume individual', 'Higienizados profissionalmente'],
  },
  {
    icon: 'ri-radio-line',
    name: 'Transmissores de Áudio',
    description: 'Transmissores FM digitais de alta potência. Cada transmissor corresponde a um canal/instrutor diferente. Cobertura total do ambiente sem interferências entre canais ou quedas de sinal.',
    specs: ['até 10 canais simultâneos', 'Cobertura até 500m', 'Tecnologia FM digital', 'Setup em 30 minutos', 'Equipamento de backup incluso'],
  },
  {
    icon: 'ri-mic-line',
    name: 'Microfones para Instrutores',
    description: 'Microfones sem fio profissionais com captação de voz cristalina. Leves e confortáveis para uso durante toda a aula. Compatíveis com headset ou lapela, conforme preferência do instrutor.',
    specs: ['Microfones wireless', 'Captação cristalina', 'Latência &lt; 5ms', 'Alcance 100m', 'Bateria longa duração'],
  },
];

export default function HowItWorksSection() {
  const { ref: stepsRef, isVisible: stepsVisible } = useScrollAnimation({ threshold: 0.1 });
  const { ref: equipRef, isVisible: equipVisible } = useScrollAnimation({ threshold: 0.05 });

  return (
    <>
      {/* How it works */}
      <section ref={stepsRef} className="py-28 bg-gray-50">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-16">
            <span className="font-body text-xs font-semibold uppercase tracking-[0.2em] text-primary mb-4 block">
              Funcionamento
            </span>
            <h2 className={`font-heading font-bold text-5xl text-secondary transition-all duration-700 ${stepsVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
              como funciona a aula silenciosa
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {steps.map((step, i) => (
              <div
                key={i}
                className={`relative bg-white rounded-3xl p-8 border border-gray-100 transition-all duration-700 ${stepsVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
                style={{ transitionDelay: `${i * 120}ms` }}
              >
                {i < steps.length - 1 && (
                  <div className="hidden lg:block absolute top-12 -right-4 z-10">
                    <i className="ri-arrow-right-line text-primary text-2xl" />
                  </div>
                )}
                <div className="font-heading font-extrabold text-5xl text-primary/15 mb-4 leading-none">{step.number}</div>
                <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-primary/10 mb-4">
                  <i className={`${step.icon} text-primary text-2xl`} />
                </div>
                <h3 className="font-heading font-bold text-lg text-secondary mb-3 leading-snug">{step.title}</h3>
                <p className="font-body text-sm text-gray-600 leading-relaxed">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Equipment included */}
      <section ref={equipRef} className="py-28 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-16">
            <span className="font-body text-xs font-semibold uppercase tracking-[0.2em] text-primary mb-4 block">
              Equipamentos
            </span>
            <h2 className={`font-heading font-bold text-5xl text-secondary mb-4 transition-all duration-700 ${equipVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
              o que está incluso
            </h2>
            <p className={`font-body text-lg text-gray-600 max-w-2xl mx-auto transition-all duration-700 delay-100 ${equipVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
              Tudo o que você precisa para uma <strong>Aula Silenciosa</strong> impecável — dos fones ao suporte técnico
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
            {equipment.map((eq, i) => (
              <div
                key={i}
                className={`bg-gray-50 rounded-3xl p-10 border border-gray-100 hover:border-primary/30 transition-all duration-500 ${equipVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
                style={{ transitionDelay: `${i * 120}ms` }}
              >
                <div className="w-16 h-16 flex items-center justify-center rounded-2xl bg-primary/10 mb-6">
                  <i className={`${eq.icon} text-primary text-3xl`} />
                </div>
                <h3 className="font-heading font-bold text-xl text-secondary mb-3">{eq.name}</h3>
                <p className="font-body text-sm text-gray-600 leading-relaxed mb-5">{eq.description}</p>
                <ul className="space-y-2.5">
                  {eq.specs.map((spec, si) => (
                    <li key={si} className="flex items-center gap-2.5 text-sm text-gray-700">
                      <i className="ri-check-line text-primary flex-shrink-0" />
                      <span>{spec}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          <div className={`bg-primary/5 border border-primary/20 rounded-2xl px-8 py-6 flex items-start gap-4 transition-all duration-500 delay-300 ${equipVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
            <div className="w-10 h-10 flex items-center justify-center flex-shrink-0">
              <i className="ri-information-line text-primary text-2xl" />
            </div>
            <div>
              <p className="font-body font-semibold text-secondary mb-1">Pacote completo, sem surpresas</p>
              <p className="font-body text-sm text-gray-700 leading-relaxed">
                Montagem, teste de som, desmontagem, suporte técnico presencial durante toda a aula, higienização dos fones e <strong>10% de fones extras como reserva</strong> — tudo incluso no serviço. Você só precisa dos instrutores e dos alunos.
              </p>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}